import { useCallback, useState } from 'react';

import type { RequestPasswordRecoveryDto } from '@identity/application/dtos';
import { getPublicUseCases } from '@identity/composition';
import { resolveIdentityErrorMessage } from '@identity/presentation/utils';
import type { IdentityErrorMessage } from '@identity/presentation/utils';

export type PasswordRecoveryRequester = {
  /** i18n key of the inline error, or `undefined` when the last request was accepted. */
  errorKey?: string;
  /** Seconds to wait, when the failure named one. The key it comes with interpolates it. */
  errorRetryAfterSeconds?: number;
  isSending: boolean;
  /** Asks for the link. It resolves `true` when the backend accepts the request. */
  request: (dto: RequestPasswordRecoveryDto) => Promise<boolean>;
};

/**
 * Asks for a recovery link and keeps the inline error of the last attempt.
 *
 * The capture view and the resend both need the same three values, and it resolves a boolean instead
 * of throwing so a click handler needs no `try/catch`. A rejected request disables nothing for a
 * while: the rate limit arrives as a code, the screen shows its message, and the user can try again.
 * The message says how long to wait when the failure names it, but there is no countdown: the daily
 * cap outlives the visit, and nobody watches a clock for it.
 *
 * It does not go through a slice: this is the one call of the domain that authorizes with nothing, so
 * it has no credential to pick and no account to report.
 */
export const useRequestPasswordRecovery = (): PasswordRecoveryRequester => {
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<IdentityErrorMessage | undefined>(undefined);

  const request = useCallback(async (dto: RequestPasswordRecoveryDto): Promise<boolean> => {
    setIsSending(true);
    setError(undefined);

    try {
      await getPublicUseCases().requestPasswordRecovery.execute(dto);

      return true;
    } catch (failure) {
      setError(resolveIdentityErrorMessage(failure));

      return false;
    } finally {
      setIsSending(false);
    }
  }, []);

  return { errorKey: error?.i18nKey, errorRetryAfterSeconds: error?.retryAfterSeconds, isSending, request };
};
